import { useEffect, useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Pause, Play, Square } from 'lucide-react';
import { DinoLibrarian } from '@/components/DinoLibrarian';
import { FocusMilestonePopup } from '@/components/FocusMilestonePopup';

interface FocusTimerProps {
  durationMinutes: number;
  onComplete: (elapsedSeconds: number) => void;
  onStop: (elapsedSeconds: number) => void;
  isPaused: boolean;
  onPauseToggle: () => void;
  pose: 'shelving' | 'organizing' | 'reading' | 'stamping' | 'tidying';
  sessionId: string | null;
  onMilestoneReached?: (milestone: number) => void;
}

export function FocusTimer({ 
  durationMinutes, 
  onComplete, 
  onStop, 
  isPaused, 
  onPauseToggle, 
  pose,
  sessionId,
  onMilestoneReached
}: FocusTimerProps) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentMilestone, setCurrentMilestone] = useState<{ percent: number; xp: number } | null>(null);
  const [reachedMilestones, setReachedMilestones] = useState<Set<number>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const totalSeconds = durationMinutes * 60;
  const remainingSeconds = Math.max(0, totalSeconds - elapsedSeconds);
  const progress = (elapsedSeconds / totalSeconds) * 100;
  
  const MILESTONES = [25, 50, 75];
  const MILESTONE_XP = 5;

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Check for milestones
  useEffect(() => {
    const currentProgress = (elapsedSeconds / totalSeconds) * 100;
    
    for (const milestone of MILESTONES) {
      if (currentProgress >= milestone && !reachedMilestones.has(milestone)) {
        setReachedMilestones(prev => new Set(prev).add(milestone));
        setCurrentMilestone({ percent: milestone, xp: MILESTONE_XP });
        onMilestoneReached?.(milestone);
        break;
      }
    }
  }, [elapsedSeconds, totalSeconds, reachedMilestones, onMilestoneReached]);

  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => {
          const next = prev + 1;
          if (next >= totalSeconds) {
            clearInterval(intervalRef.current!);
            onComplete(totalSeconds);
            return totalSeconds;
          }
          return next;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, totalSeconds, onComplete]);

  // Page visibility detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && !isPaused) {
        onPauseToggle();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPaused, onPauseToggle]);

  // SVG circle properties
  const size = 280;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <>
      {/* Milestone Popup */}
      {currentMilestone && (
        <FocusMilestonePopup
          milestone={currentMilestone.percent}
          xpAwarded={currentMilestone.xp}
          onClose={() => setCurrentMilestone(null)}
        />
      )}

      <div className="flex flex-col items-center gap-6">
      {/* Circular Timer */}
      <div className="relative" style={{ width: size, height: size }}>
        {/* Dino video background - circular clipped */}
        <div 
          className="absolute inset-0 overflow-hidden rounded-full bg-background"
        >
          <div className="w-full h-full flex items-center justify-center scale-[1.8]">
            <DinoLibrarian pose={pose} size={size} />
          </div>
        </div>

        <svg width={size} height={size} className="transform -rotate-90 relative z-10">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="hsl(var(--muted) / 0.3)"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="url(#gradient)"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className={isPaused ? '' : 'animate-pulse'}
            style={{ transition: 'stroke-dashoffset 1s linear' }}
          />
          {/* Gradient definition */}
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(45, 100%, 76%)" />
              <stop offset="100%" stopColor="hsl(82, 60%, 34%)" />
            </linearGradient>
          </defs>
        </svg>

        {/* Time display overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none">
          <div className="text-5xl font-bold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] font-heading">
            {formatTime(remainingSeconds)}
          </div>
          <div className="text-sm text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] mt-2 font-semibold">
            {isPaused ? 'Paused' : 'Focus Time'}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <Button
          onClick={onPauseToggle}
          size="lg"
          variant="outline"
          className="btn-pill w-32"
        >
          {isPaused ? (
            <>
              <Play className="w-5 h-5 mr-2" />
              Resume
            </>
          ) : (
            <>
              <Pause className="w-5 h-5 mr-2" />
              Pause
            </>
          )}
        </Button>
        <Button
          onClick={() => onStop(elapsedSeconds)}
          size="lg"
          variant="outline"
          className="btn-pill"
        >
          <Square className="w-5 h-5 mr-2" />
          Stop
        </Button>
      </div>
    </div>
    </>
  );
}
